import { useState } from 'react';
import { SEOHead } from '../../components/SEOHead';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Helixara",
    "description":
      "AI-powered digital transformation consulting across biotech, banking, labs, and logistics. We guide organizations from legacy to intelligent systems.",
    "url": import.meta.env.VITE_SITE_URL || "https://example.com",
    "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-123-4567",
      "contactType": "customer service",
      "email": "contact@helixara.com",
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Innovation Drive",
      "addressLocality": "Boston",
      "addressRegion": "MA",
      "postalCode": "02115",
      "addressCountry": "US",
    },
    "sameAs": [
      "https://linkedin.com/company/helixara",
      "https://twitter.com/helixara",
    ],
    "founder": {
      "@type": "Person",
      "name": "Dr. Jane Smith",
      "jobTitle": "Founder & CEO",
    },
    "services": [
      {
        "@type": "Service",
        "name": "Digital Biology & AI",
        "description":
          "AI-driven biological systems for drug discovery and molecular engineering",
      },
      {
        "@type": "Service",
        "name": "Digital Transformation",
        "description":
          "Intelligent automation and AI integration across industries",
      },
      {
        "@type": "Service",
        "name": "Digital Wellness",
        "description":
          "AI-powered wellness solutions and digital health systems",
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Helixara - AI-Powered Digital Transformation Consulting"
        description="Helixara empowers organizations across biotech, banking, labs, and logistics with AI, cloud, and automation solutions. We guide the shift from legacy to intelligent systems for breakthrough innovation."
        keywords="AI consulting, digital transformation, cloud automation, biotech innovation, banking technology, logistics optimization, intelligent systems, data analytics"
        ogTitle="Helixara - AI-Powered Digital Transformation Consulting"
        ogDescription="Helixara empowers organizations across biotech, banking, labs, and logistics with AI, cloud, and automation solutions. We guide the shift from legacy to intelligent systems for breakthrough innovation."
        ogImage={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/og-image.jpg`}
        canonicalUrl={import.meta.env.VITE_SITE_URL || "https://example.com"}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-blue-800 shadow-sm border-b border-blue-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20 md:h-24">
              <div className="flex items-center">
                <div className="flex items-center space-x-4 md:space-x-6">
                  <img
                    src="https://static.readdy.ai/image/d80b109f077bb32eaa0599f7baacf71d/8bce5177d9ebd993ca678e3948381173.png"
                    alt="Helixara Logo"
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-contain"
                  />
                  <div className="text-left">
                    <h1
                      className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight"
                      style={{
                        fontFamily:
                          '"Orbitron", "Exo 2", "Rajdhani", "Space Grotesk", monospace',
                        fontWeight: "700",
                        letterSpacing: "0.05em",
                        textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      HELIXARA
                    </h1>
                    <p 
                      className="text-xs md:text-sm lg:text-base font-semibold tracking-wide -mt-1 leading-tight text-blue-100"
                      style={{
                        fontFamily: '"Exo 2", "Rajdhani", "Space Grotesk", sans-serif',
                        fontWeight: "600",
                        letterSpacing: "0.08em",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      Designing Life. Decoding Healing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-8">
                  <a
                    href="#services"
                    className="text-blue-100 hover:text-white px-3 py-2 text-sm font-medium cursor-pointer transition-colors duration-300"
                  >
                    Services
                  </a>
                  <a
                    href="#solutions"
                    className="text-blue-100 hover:text-white px-3 py-2 text-sm font-medium cursor-pointer transition-colors duration-300"
                  >
                    Solutions
                  </a>
                  <a
                    href="#about"
                    className="text-blue-100 hover:text-white px-3 py-2 text-sm font-medium cursor-pointer transition-colors duration-300"
                  >
                    About
                  </a>
                  <a
                    href="#contact"
                    className="text-blue-100 hover:text-white px-3 py-2 text-sm font-medium cursor-pointer transition-colors duration-300"
                  >
                    Contact
                  </a>
                  <button className="bg-white text-blue-800 px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 whitespace-nowrap cursor-pointer transition-colors duration-300">
                    Get Started
                  </button>
                </div>
              </div>

              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-blue-100 hover:text-white cursor-pointer"
                >
                  <i className="ri-menu-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-blue-800 border-t border-blue-700">
                <a
                  href="#services"
                  className="text-blue-101 hover:text-white block px-3 py-2 text-base font-medium cursor-pointer"
                >
                  Services
                </a>
                <a
                  href="#solutions"
                  className="text-blue-101 hover:text-white block px-3 py-2 text-base font-medium cursor-pointer"
                >
                  Solutions
                </a>
                <a
                  href="#about"
                  className="text-blue-101 hover:text-white block px-3 py-2 text-base font-medium cursor-pointer"
                >
                  About
                </a>
                <a
                  href="#contact"
                  className="text-blue-101 hover:text-white block px-3 py-2 text-base font-medium cursor-pointer"
                >
                  Contact
                </a>
                <button className="bg-white text-blue-800 px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 whitespace-nowrap cursor-pointer ml-3 mt-2">
                  Get Started
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section
          className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center bg-gray-50"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.9)), url('https://public.readdy.ai/ai/img_res/bd1a094fb405809d5dbe6c4d84340267.jpg')`,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Content */}
              <div className="text-left">
                <div className="mb-8">
                  <span className="inline-block bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm text-blue-700 px-8 py-3 rounded-full text-sm font-medium border border-blue-200/50 shadow-lg relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent"></div>
                    <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-blue-300/50 to-transparent"></div>
                    <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-blue-300/50 to-transparent"></div>
                    <span className="relative z-10">Strategic AI Consultancy & Advisory</span>
                  </span>
                </div>

                <div className="mb-8">
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 leading-tight tracking-tight">
                    <span className="block text-gray-900 mb-3 font-extralight" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                      Designing Life.
                    </span>
                    <span className="block text-blue-800 mb-3 font-extralight" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                      Decoding Healing.
                    </span>
                  </h1>

                  <div className="mb-8">
                    <p className="text-xl md:text-2xl lg:text-3xl font-light text-blue-700 tracking-wider leading-relaxed" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif', letterSpacing: '0.05em' }}>
                      Design • Decode • Deliver
                    </p>
                  </div>
                </div>

                <div className="mb-8">
                  <p className="text-base md:text-lg lg:text-xl mb-6 leading-relaxed text-gray-800 font-normal max-w-2xl" style={{ fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif', lineHeight: '1.7' }}>
                    Helixara empowers organizations across biotech, banking, labs, and logistics through strategic AI consultancy and digital transformation advisory. We guide the intelligent evolution from legacy systems to breakthrough innovation.
                  </p>

                  <p className="text-base md:text-lg lg:text-xl mb-12 leading-relaxed text-blue-700 font-medium italic max-w-2xl" style={{ fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif', lineHeight: '1.7' }}>
                    Because designing life means building adaptive systems, and decoding healing means using data to elevate human potential. Living well isn't a luxury — it's a science.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-16">
                  <button className="bg-blue-700 text-white px-8 py-4 rounded-xl text-base md:text-lg font-medium whitespace-nowrap cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:bg-blue-800" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                    Strategic Consultancy
                  </button>
                  <button className="bg-white text-blue-700 px-8 py-4 rounded-xl text-base md:text-lg font-medium whitespace-nowrap cursor-pointer border-2 border-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:bg-blue-50" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                    AI Advisory Services
                  </button>
                </div>

                {/* Key Pillars */}
                <div className="grid gap-4">
                  <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-6 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-2xl"></div>
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-settings-4-line text-2xl text-indigo-600"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>Design</h3>
                          <p className="text-sm text-gray-700 font-normal leading-relaxed" style={{ fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                            Building intelligent systems that adapt and evolve with your business
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-6 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-2xl"></div>
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-bar-chart-box-line text-2xl text-indigo-600"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>Decode</h3>
                          <p className="text-sm text-gray-700 font-normal leading-relaxed" style={{ fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                            Transforming complex data into actionable insights and opportunities
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-6 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-2xl"></div>
                    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                    <div className="relative z-10">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-rocket-line text-2xl text-indigo-600"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>Deliver</h3>
                          <p className="text-sm text-gray-700 font-normal leading-relaxed" style={{ fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                            Empowering organizations to unlock their full potential through
                            innovation
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Content - McKinsey-Inspired Boardroom */}
              <div className="relative">
                {/* Professional AI-Powered Drug Discovery Video */}
                <div className="mb-8">
                  <div className="relative rounded-3xl overflow-hidden shadow-lg">
                    <video
                      className="w-full h-64 object-cover"
                      controls
                      poster="https://readdy.ai/api/search-image?query=Professional%20pharmaceutical%20boardroom%20presentation%20with%20senior%20executives%20and%20investors%20reviewing%20AI-driven%20drug%20discovery%20portfolio%2C%20sophisticated%20corporate%20conference%20room%20with%20large%20displays%20showing%20drug%20development%20pipeline%20metrics%20ROI%20analysis%20and%20clinical%20trial%20success%20rates%2C%20diverse%20C-suite%20leadership%20team%20in%20premium%20business%20attire%20discussing%20strategic%20pharmaceutical%20investments%20and%20AI%20technology%20implementation%2C%20elegant%20executive%20meeting%20room%20with%20polished%20conference%20table%20and%20advanced%20presentation%20technology%2C%20representing%20investor%E2%80%91grade%20pharmaceutical%20consulting%20and%20strategic%20drug%20development%20advisory%20services%20with%20crystal%E2%80%91clear%20professional%20corporate%20photography&width=600&height=256&seq=investor‑pharma‑boardroom‑video&orientation=landscape"
                    >
                      <source src="https://readdy.ai/api/search-video?query=Professional pharmaceutical investment presentation showcasing Helixara AI‑powered drug discovery solutions with executive team demonstrating ROI metrics clinical trial optimization and strategic drug discovery consulting services, sophisticated boardroom environment with senior leadership presenting to investors about artificial intelligence implementation in pharmaceutical research and development, premium corporate setting highlighting strategic consulting excellence and measurable business outcomes in drug discovery innovation&duration=120&width=600&height=256&seq=helixara‑investor‑pharma‑video" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 to-transparent rounded-3xl pointer-events-none"></div>
                    
                    <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-white text-xs font-medium">2:00</span>
                    </div>
                  </div>
                </div>

                <img
                  src="https://readdy.ai/api/search-image?query=Advanced%20pharmaceutical%20AI%20research%20laboratory%20with%20diverse%20scientists%20and%20computational%20biologists%20working%20at%20computer%20workstations%20displaying%20molecular%20structures%20and%20drug%20discovery%20algorithms%2C%20modern%20laboratory%20environment%20with%20multiple%20computer%20screens%20showing%20AI%E2%80%91powered%20drug%20development%20software%20and%20machine%20learning%20models%2C%20professional%20research%20team%20in%20lab%20coats%20collaborating%20around%20sophisticated%20computer%20setups%20and%20digital%20displays%2C%20cutting%E2%80%91edge%20drug%20discovery%20lab%20featuring%20scientists%20focusing%20on%20computer%20monitors%20displaying%20protein%20folding%20predictions%20chemical%20compound%20optimization%20and%20AI%E2%80%91driven%20drug%20development%20workflows%2C%20representing%20next%E2%80%91generation%20pharmaceutical%20innovation%20with%20people%20working%20on%20computers%20and%20digital%20drug%20discovery%20systems&width=600&height=800&seq=ai‑drug‑discovery‑people‑computers&orientation=portrait"
                  alt="Advanced AI‑Powered Pharmaceutical Drug Discovery Laboratory"
                  className="rounded-3xl shadow-lg object-cover w-full h-110 object-top"
                />
              </div>

            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-6">
                AI-Powered Strategic Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                Transforming industries through intelligent systems that design life, decode
                healing, and deliver precision solutions across Digital Biology, Digital
                Transformation, and Digital Wellness
              </p>
            </div>

            {/* Silicon Valley AI Innovation Hub Banner */}
            <div className="mb-20">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Breathtaking%20Silicon%20Valley%20panoramic%20view%20with%20iconic%20tech%20campus%20buildings%20and%20San%20Francisco%20Bay%20featuring%20state-of-the-art%20AI%20research%20facility%20in%20foreground%2C%20cutting%E2%80%91edge%20artificial%20intelligence%20laboratory%20with%20holographic%20displays%20showing%20advanced%20ML%20algorithms%20and%20digital%20transformation%20analytics%2C%20futuristic%20California%20technology%20headquarters%20with%20clean%20minimalist%20architecture%20and%20floor%E2%80%91to%E2%80%91ceiling%20glass%20walls%2C%20diverse%20team%20of%20AI%20engineers%20and%20data%20scientists%20working%20with%20sophisticated%20ML%20systems%20for%20digital%20transformation%20consulting%2C%20beautiful%20Silicon%20Valley%20landscape%20with%20rolling%20hills%20and%20modern%20tech%20campuses%2C%20representing%20fusion%20of%20California%20innovation%20excellence%20with%20cutting%E2%80%91edge%20AI%20technology%20and%20digital%20analytics%20leadership%2C%20golden%20hour%20natural%20lighting%20creating%20warm%20professional%20atmosphere%20with%20crystal%E2%80%91clear%20ultra%E2%80%91high%20definition%20clarity&width=1200&height=400&seq=enhanced‑swiss‑ai‑hub&orientation=landscape"
                  alt="Enhanced Silicon Valley AI Digital Analytics Innovation Hub"
                  className="rounded-3xl shadow-lg object-cover w-full h-64 object-center"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent rounded-3xl"></div>
                
                {/* Enhanced Strategic Solutions Technology Elements */}
                <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-dna-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-blue-800">Digital Biology</div>
                      <div className="text-xs text-blue-600">AI Drug Discovery</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                      <i className="ri-robot-2-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-indigo-800">Digital Transformation</div>
                      <div className="text-xs text-indigo-600">AI Automation</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-rose-600 rounded-full flex items-center justify-center">
                      <i className="ri-heart-pulse-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-rose-800">Digital Wellness</div>
                      <div className="text-xs text-rose-600">AI Health Solutions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-blue-100">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-dna-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-light text-blue-800 mb-4">
                  Digital Biology &amp; AI
                </h3>
                <p className="text-gray-700 mb-6 font-light leading-relaxed">
                  Harness artificial intelligence to design breakthrough therapies and decode
                  molecular pathways. Our AI-driven biological systems accelerate drug
                  discovery, optimize therapeutic targets, and transform pharmaceutical research
                  through intelligent molecular engineering and precision medicine solutions.
                </p>
                <button className="text-blue-600 font-medium hover:blue-700 cursor-pointer transition-colors duration-300">
                  Design Life <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>

              <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-indigo-100">
                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-robot-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-light text-indigo-800 mb-4">
                  Digital Transformation
                </h3>
                <p className="text-gray-700 mb-6 font-light leading-relaxed">
                  Revolutionize organizational capabilities through intelligent automation and AI
                  integration. We guide enterprises across Manufacturing, Finance,
                  Research, Automotive, Healthcare, and Retail industries through comprehensive digital evolution
                  optimizing processes and unlocking innovation potential.
                </p>
                <button className="text-indigo-600 font-medium hover:indigo-700 cursor-pointer transition-colors duration-300">
                  Transform Operations <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>

              <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-rose-100">
                <div className="w-16 h-16 bg-rose-600 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-heart-pulse-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-light text-rose-800 mb-4">
                  Digital Wellness
                </h3>
                <p className="text-gray-700 mb-6 font-light leading-relaxed">
                  Advance personalized healthcare through AI-powered wellness solutions and
                  evidence-based digital health. We decode healing pathways to deliver
                  intelligent health systems that make precision wellness accessible,
                  transforming patient care through data-driven therapeutic interventions.
                </p>
                <button className="text-rose-600 font-medium hover:rose-700 cursor-pointer transition-colors duration-300">
                  Decode Healing <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Section */}
        <section id="solutions" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="bg-white p-10 rounded-3xl shadow-sm border border-gray-100">
                <h2 className="text-4xl font-light text-gray-800 mb-8">
                  Digital Biology &amp; AI-Powered Drug Development Solutions
                </h2>
                <p className="text-xl text-gray-600 mb-10 font-light leading-relaxed">
                  Strategic consultancy and technical support services guiding pharmaceutical
                  organizations through AI-driven drug development processes, from target
                  identification to post-market surveillance, leveraging digital biology and
                  intelligent systems for breakthrough therapeutic discoveries.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4 bg-blue-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        AI-Based Target Identification
                      </h4>
                      <p className="text-gray-600 font-light">
                        Strategic guidance for implementing AI algorithms in target discovery,
                        protein‑protein interaction mapping, and pathway analysis for enhanced
                        therapeutic target validation
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-emerald-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        Digital Library Screening &amp; Lead Optimization
                      </h4>
                      <p className="text-gray-600 font-light">
                        Technical support for virtual compound libraries, AI-driven molecular
                        screening, and machine learning‑based lead compound optimization strategies
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-purple-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        AI-Enhanced Formulation Development
                      </h4>
                      <p className="text-gray-600 font-light">
                        Consultancy for implementing predictive modeling in drug formulation,
                        stability testing optimization, and AI‑assisted delivery system design
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-orange-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        Biomarker Discovery &amp; Omics Studies
                      </h4>
                      <p className="text-gray-600 font-light">
                        Strategic support for AI‑powered genomics, proteomics, and metabolomics
                        analysis enabling precision medicine approaches and personalized therapeutic
                        strategies
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-teal-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        Digital Clinical Development &amp; Post-Market Intelligence
                      </h4>
                      <p className="text-gray-600 font-light">
                        Guidance for AI‑driven clinical trial design, real‑world evidence
                        generation, and intelligent post‑market surveillance systems for comprehensive
                        drug lifecycle management
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-cyan-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        cGMP Manufacturing &amp; Quality Consultancy
                      </h4>
                      <p className="text-gray-600 font-light">
                        Strategic guidance for current Good Manufacturing Practice (cGMP) compliance, 
                        quality management systems implementation, and AI‑enhanced manufacturing 
                        process optimization for pharmaceutical production excellence
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-amber-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">
                        Equipment, Chemistries &amp; Lab Informatics
                      </h4>
                      <p className="text-gray-600 font-light">
                        Comprehensive support for NextGen Laboratory &amp; Manufacturing equipment, 
                        specialized chemistry &amp; reagents sourcing, and advanced lab informatics 
                        systems. Strategic guidance for equipment qualification, chemistry optimization, 
                        and LIMS implementation to maximize Pharma-Biotech research capabilities
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                {/* User Stories - Horizontal Layout */}
                <div className="mb-8 grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 shadow-sm">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src="https://readdy.ai/api/search-image?query=Professional%20female%20scientist%20Dr%20Sarah%20Chen%20in%20premium%20business%20attire%20standing%20in%20modern%20pharmaceutical%20laboratory%2C%20confident%20Asian-American%20woman%20with%20PhD%20credentials%20wearing%20elegant%20blazer%20in%20state-of-the-art%20biotech%20research%20facility%2C%20sophisticated%20corporate%20headshot%20of%20Chief%20Scientific%20Officer%20in%20clean%20laboratory%20environment%20with%20advanced%20equipment%2C%20professional%20portrait%20photography%20with%20warm%20lighting%20and%20contemporary%20pharmaceutical%20setting&width=80&height=80&seq=dr-sarah-chen-cso&orientation=squarish"
                          alt="Dr. Sarah Chen, Chief Scientific Officer"
                          className="w-12 h-12 rounded-full object-cover shadow-sm"
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-800 mb-2">Dr. Sarah Chen, Chief Scientific Officer</h4>
                        <p className="text-gray-600 font-light leading-relaxed">
                          "Helixara's AI-powered drug discovery platform reduced our target identification time by 60%. Their strategic guidance helped us implement machine learning algorithms that identified three promising therapeutic targets in just 8 weeks."
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-100 shadow-sm">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src="https://readdy.ai/api/search-image?query=Professional%20male%20executive%20Dr%20Michael%20Rodriguez%20in%20premium%20business%20suit%20standing%20in%20modern%20pharmaceutical%20boardroom%2C%20confident%20Hispanic-American%20man%20with%20VP%20credentials%20wearing%20elegant%20navy%20suit%20in%20sophisticated%20corporate%20environment%2C%20executive%20portrait%20of%20VP%20of%20Research%20in%20contemporary%20office%20setting%20with%20pharmaceutical%20displays%2C%20professional%20headshot%20photography%20with%20warm%20lighting%20and%20modern%20business%20atmosphere&width=80&height=80&seq=dr-michael-rodriguez-vp&orientation=squarish"
                          alt="Dr. Michael Rodriguez, VP of Research"
                          className="w-12 h-12 rounded-full object-cover shadow-sm"
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-800 mb-2">Dr. Michael Rodriguez, VP of Research</h4>
                        <p className="text-gray-600 font-light leading-relaxed">
                          "The digital biology consultancy transformed our drug development pipeline. Helixara's AI-enhanced formulation development approach accelerated our lead optimization process and improved our success rate by 45%."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional AI-Powered Drug Discovery Video */}
                <div className="mb-8">
                  <div className="relative rounded-3xl overflow-hidden shadow-lg">
                    <video
                      className="w-full h-64 object-cover"
                      controls
                      poster="https://readdy.ai/api/search-image?query=Professional%20pharmaceutical%20boardroom%20presentation%20with%20senior%20executives%20and%20investors%20reviewing%20AI-driven%20drug%20discovery%20portfolio%2C%20sophisticated%20corporate%20conference%20room%20with%20large%20displays%20showing%20drug%20development%20pipeline%20metrics%20ROI%20analysis%20and%20clinical%20trial%20success%20rates%2C%20diverse%20C-suite%20leadership%20team%20in%20premium%20business%20attire%20discussing%20strategic%20pharmaceutical%20investments%20and%20AI%20technology%20implementation%2C%20elegant%20executive%20meeting%20room%20with%20polished%20conference%20table%20and%20advanced%20presentation%20technology%2C%20representing%20investor%E2%80%91grade%20pharmaceutical%20consulting%20and%20strategic%20drug%20development%20advisory%20services%20with%20crystal%E2%80%91clear%20professional%20corporate%20photography&width=600&height=256&seq=investor‑pharma‑boardroom‑video&orientation=landscape"
                    >
                      <source src="https://readdy.ai/api/search-video?query=Professional pharmaceutical investment presentation showcasing Helixara AI‑powered drug discovery solutions with executive team demonstrating ROI metrics clinical trial optimization and strategic drug discovery consulting services, sophisticated boardroom environment with senior leadership presenting to investors about artificial intelligence implementation in pharmaceutical research and development, premium corporate setting highlighting strategic consulting excellence and measurable business outcomes in drug discovery innovation&duration=120&width=600&height=256&seq=helixara‑investor‑pharma‑video" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 to-transparent rounded-3xl pointer-events-none"></div>
                    
                    <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-white text-xs font-medium">2:00</span>
                    </div>
                  </div>
                </div>

                <img
                  src="https://readdy.ai/api/search-image?query=State-of-the-art%20pharmaceutical%20AI%20research%20laboratory%20with%20diverse%20scientists%20and%20computational%20biologists%20working%20at%20computer%20workstations%20displaying%20molecular%20structures%20and%20drug%20discovery%20algorithms%2C%20modern%20laboratory%20environment%20with%20multiple%20computer%20screens%20showing%20AI%E2%80%91powered%20drug%20discovery%20software%20and%20machine%20learning%20models%2C%20professional%20research%20team%20in%20lab%20coats%20collaborating%20around%20sophisticated%20computer%20setups%20and%20digital%20displays%2C%20cutting%E2%80%91edge%20drug%20discovery%20lab%20featuring%20scientists%20focusing%20on%20computer%20monitors%20displaying%20protein%20folding%20predictions%20chemical%20compound%20optimization%20and%20AI%E2%80%91driven%20drug%20development%20workflows%2C%20representing%20next%E2%80%91generation%20pharmaceutical%20innovation%20with%20people%20working%20on%20computers%20and%20digital%20drug%20discovery%20systems&width=600&height=440&seq=ai‑drug‑discovery‑people‑computers&orientation=portrait"
                  alt="Advanced AI‑Powered Pharmaceutical Drug Discovery Laboratory"
                  className="rounded-3xl shadow-lg object-cover w-full h-110 object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Technology Section */}
        <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-6">
                Digital Transformation Technology Services
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto font-light">
                Comprehensive technology consultancy empowering Manufacturing, Finance,
                Research, Automotive, Healthcare, and Retail industries through intelligent
                digital transformation solutions
              </p>
            </div>

            {/* Enhanced Budapest AI-ML Technology Banner */}
            <div className="mb-20">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Spectacular%20Budapest%20cityscape%20with%20iconic%20Hungarian%20Parliament%20Building%20and%20Danube%20River%20featuring%20state-of-the-art%20AI%20research%20facility%20in%20foreground%2C%20cutting%E2%80%91edge%20artificial%20intelligence%20laboratory%20with%20holographic%20displays%20showing%20advanced%20ML%20algorithms%20and%20digital%20transformation%20analytics%2C%20futuristic%20California%20technology%20headquarters%20with%20clean%20minimalist%20architecture%20and%20floor%E2%80%91to%E2%80%91ceiling%20glass%20walls%2C%20diverse%20team%20of%20AI%20engineers%20and%20data%20scientists%20working%20with%20sophisticated%20ML%20systems%20for%20digital%20transformation%20consulting%2C%20beautiful%20Budapest%20landscape%20with%20rolling%20hills%20and%20modern%20tech%20campuses%2C%20representing%20fusion%20of%20California%20innovation%20excellence%20with%20cutting%E2%80%91edge%20AI%20technology%20and%20digital%20analytics%20leadership%2C%20golden%20hour%20natural%20lighting%20creating%20warm%20professional%20atmosphere%20with%20crystal%E2%80%91clear%20ultra%E2%80%91high%20definition%20clarity&width=1200&height=600&seq=enhanced‑swiss‑ai‑hub&orientation=landscape"
                  alt="Enhanced Budapest AI‑ML Digital Analytics Innovation Hub"
                  className="rounded-3xl shadow-lg object-cover w-full h-80 object-center"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent rounded-3xl"></div>

                {/* Floating AI‑ML Technology Strategy Elements */}
                <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-brain-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-blue-800">Machine Learning</div>
                      <div className="text-xs text-blue-600">AI Model Development</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <i className="ri-cpu-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-purple-800">Deep Learning</div>
                      <div className="text-xs text-purple-600">Neural Networks</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                      <i className="ri-bar-chart-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-emerald-800">Data Analytics</div>
                      <div className="text-xs text-emerald-600">Predictive Modeling</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-2xl border border-blue-100 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                      <i className="ri-robot-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-indigo-800">AI Automation</div>
                      <div className="text-xs text-indigo-600">Intelligent Systems</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-cloud-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-blue-800 mb-4">
                    Cloud &amp; SaaS Consultancy
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Strategic guidance for cloud migration, SaaS implementation, and hybrid
                    infrastructure optimization across manufacturing, finance, and research
                    sectors
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    AWS • Azure • Google Cloud • Salesforce • Microsoft 365
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-bar-chart-box-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-indigo-800 mb-4">
                    Advanced Data Analytics
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Big data processing, predictive analytics, and business intelligence
                    solutions for automotive, healthcare, and financial services organizations
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    Tableau • Power BI • Snowflake • Apache Spark • Databricks
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-600 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-robot-2-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-emerald-800 mb-4">
                    Robotics &amp; Automation
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Process automation, robotic process automation (RPA), and intelligent
                    workflow optimization for manufacturing and automotive industries
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    UiPath • Blue Prism • Automation Anywhere • ABB Robotics
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-600 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-settings-4-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-purple-800 mb-4">
                    SAP &amp; ERP Solutions
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Enterprise resource planning implementation, SAP consulting, and business
                    process optimization for large-scale manufacturing and finance operations
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    SAP S/5HANA • Oracle ERP • Microsoft Dynamics • NetSuite
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-600 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-cyan-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-brain-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-cyan-800 mb-4">
                    Machine Learning &amp; AI
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Custom AI model development, machine learning implementation, and intelligent
                    system integration for research, healthcare, and automotive sectors
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    TensorFlow • PyTorch • Azure ML • AWS SageMaker • H2O.ai
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-600 border border-blue-200/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-cyan-100/20 rounded-3xl"></div>
                <div className="absolute top-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute bottom-0 left-1 w-full h-px bg-gradient-to-r fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b fromtransparent via-blue-300/50 totransparent"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-medium text-teal-800 mb-4">
                    Digital Security &amp; Compliance
                  </h3>
                  <p className="text-gray-700 mb-4 font-light leading-relaxed">
                    Cybersecurity frameworks, regulatory compliance, and data governance
                    solutions ensuring secure digital transformation across all industries
                  </p>
                  <div className="text-sm text-gray-600 font-light">
                    ISO 27001 • GDPR • SOX • HIPAA • Automotive SPICE
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Digital Wellness Service Section */}
        <section className="py-24 bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-6">
                Digital Wellness Service
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto font-light">
                AI‑driven health technology and wellness solutions for personalized and data‑centric health experiences.
              </p>
            </div>

            {/* Enhanced Nordic Cycling Trail Banner */}
            <div className="mb-20">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Breathtaking%20Nordic%20landscape%20with%20dramatic%20snow-capped%20mountains%20and%20pristine%20fjords%2C%20professional%20cyclist%20in%20premium%20gear%20riding%20high-end%20mountain%20bike%20along%20scenic%20trail%2C%20golden%20hour%20lighting%20creating%20warm%20atmospheric%20glow%2C%20traditional%20Nordic%20pine%20and%20birch%20forests%20surrounding%20winding%20trail%2C%20emerald%20lake%20and%20granite%20cliffs%2C%20cyclist%20wearing%20advanced%20fitness%20smartwatch%2C%20professional%20outdoor%20sports%20photography%20with%20cinematic%20composition%2C%20ultra%E2%80%91high%20definition%20photography&width=1200&height=400&seq=enhanced-nordic-cycling-trail&orientation=landscape"
                  alt="Enhanced Professional Nordic Mountain Cycling Trail in Natural Golden Hour Light"
                  className="rounded-3xl shadow-lg object-cover w-full h-64 object-center"
                />
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="bg-white p-10 rounded-3xl shadow-sm border border-gray-100">
                <h3 className="text-3xl font-light text-gray-800 mb-8">
                  Digital Health Technology Consultancy
                </h3>
                <p className="text-xl text-gray-600 mb-10 font-light leading-relaxed">
                  Our comprehensive digital health consulting framework provides strategic guidance for
                  implementing health technology solutions, regulatory compliance advisory,
                  and evidence‑based wellness program development that supports organizations in
                  their digital health transformation journey.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4 bg-rose-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-rose-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">Health Data Analytics Guidance</h4>
                      <p className="text-gray-600 font-light">
                        Strategic consulting for implementing health analytics platforms, data
                        visualization tools, and wellness tracking system integration for organizational
                        health programs
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-purple-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">Digital Wellness Program Advisory</h4>
                      <p className="text-gray-600 font-light">
                        Guidance for developing evidence‑based digital wellness programs, mobile health
                        app integration, and employee wellness technology implementation
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-pink-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-pink-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">Health Technology Infrastructure</h4>
                      <p className="text-gray-600 font-light">
                        Technical advisory for secure health data management systems, privacy compliance
                        frameworks, and wellness platform architecture design
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 bg-indigo-50 p-6 rounded-xl">
                    <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 mb-2">Regulatory Compliance Advisory</h4>
                      <p className="text-gray-600 font-light">
                        Comprehensive guidance for health technology regulations, data privacy compliance,
                        and wellness program standards ensuring adherent implementation
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20Japanese%20wellness%20scene%20with%20diverse%20athletes%20wearing%20advanced%20fitness%20wearable%20devices%20during%20outdoor%20activities%20in%20stunning%20natural%20Japanese%20landscape%2C%20professional%20runners%20and%20hikers%20using%20cutting%E2%80%91edge%20smartwatches%20and%20fitness%20trackers%20in%20beautiful%20mountain%20scenery%20with%20traditional%20Japanese%20elements%2C%20Mount%20Fuji%20visible%20in%20background%2C%20diverse%20professionals%20monitoring%20health%20data%20with%20state%E2%80%91of%E2%80%91the%E2%80%91art%20wearable%20technology%20while%20exercising%20in%20pristine%20Japanese%20wilderness%2C%20golden%20hour%20lighting%20creating%20warm%20professional%20atmosphere%2C%20representing%20seamless%20integration%20of%20digital%20health%20technology%20in%20authentic%20Japanese%20outdoor%20wellness%20lifestyle%2C%20ultra%E2%80%91high%20definition%20professional%20sports%20photography%20with%20cinematic%20composition%20and%20natural%20lighting&width=600&height=800&seq=enhanced-japanese-wellness-wearables&orientation=portrait"
                  alt="Enhanced Professional Japanese Wellness with Advanced Wearable Technology"
                  className="rounded-3xl shadow-lg object-cover w-full h-96 lg:h-full object-top"
                />
              </div>
            </div>

            {/* New Specialized Services Tiles */}
            <div className="grid md:grid-cols-2 gap-8 mt-20">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 border border-green-100">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-leaf-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-medium text-gray-800 mb-4">
                  Functional Food and Nutrition Consultancy &amp; Technology Services
                </h3>

                {/* Added Functional Food Image */}
                <div className="mb-6">
                  <img
                    src="https://static.readdy.ai/image/d80b109f077bb32eaa0599f7baacf71d/48c059f839e63ff335e517e034f4fb51.png"
                    alt="Advanced Functional Food and Nutrition Technology Laboratory"
                    className="w-full h-48 object-cover rounded-xl shadow-sm"
                  />
                </div>

                <p className="text-gray-600 mb-6 font-light leading-relaxed">
                  Strategic consultancy for functional foods development, nutraceuticals optimization, 
                  and evidence‑based nutritional research programs. Comprehensive nutritional program 
                  development with personalized assessment frameworks and customized supplement protocols. 
                  Technology transfer services for scaling innovative nutrition solutions and product 
                  development covering formulation design, validation methodologies, and market 
                  commercialization strategies. All services focus on consultancy and technology guidance only.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Functional Foods Development Consultancy</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Nutraceuticals Optimization Advisory</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-teal-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Nutritional Program Development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-700 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Technology Transfer Services</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-emerald-700 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Product Development Strategy</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-teal-700 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Regulatory Compliance Advisory</span>
                  </div>
                </div>

                <button className="text-green-600 font-medium hover:text-green-700 cursor-pointer transition-colors duration-300 whitespace-nowrap">
                  Explore Nutrition Consultancy Services <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 border border-blue-100">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-sensor-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-medium text-gray-800 mb-4">
                  Next-Gen Bio Sensor Services &amp; Research Collaboration
                </h3>
                
                {/* Added Bio Sensor Image */}
                <div className="mb-6">
                  <img
                    src="https://static.readdy.ai/image/d80b109f077bb32eaa0599f7baacf71d/e26bd7e37b326b249d75192382f7c382.png"
                    alt="Advanced Bio Sensor Technology"
                    className="w-full h-48 object-cover rounded-xl shadow-sm"
                  />
                </div>
                
                <p className="text-gray-600 mb-6 font-light leading-relaxed">
                  Advanced biosensor technology consulting and research collaboration services for 
                  real-time health monitoring, molecular detection, and novel chemistry applications. 
                  Strategic partnerships for developing cutting‑edge sensor technologies and 
                  collaborative research initiatives in biotechnology innovation.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Biosensor Technology Development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Research Collaboration Programs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Molecular Detection Systems</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-700 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Novel Chemistries Development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-indigo-700 rounded-full"></div>
                    <span className="text-sm text-gray-600 font-light">Next-Gen Material Design Collaboration</span>
                  </div>
                </div>

                <button className="text-blue-600 font-medium hover:blue-700 cursor-pointer transition-colors duration-300 whitespace-nowrap">
                  Discover Bio Sensor Solutions <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 relative overflow-hidden">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-4xl font-light text-white mb-8">
              Ready to <span className="font-medium">Design Life</span> and{' '}
              <span className="font-medium">Decode Healing</span>?
            </h2>
            <p className="text-xl text-blue-100 mb-12 font-light leading-relaxed">
              Transform your organization with AI-powered strategic consulting across Digital Biology, Digital
              Transformation, and Digital Wellness. From drug discovery and clinical optimization, to intelligent
              automation and evidence-based wellness solutions - we guide your journey from legacy systems to breakthrough
              innovation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-50 whitespace-nowrap cursor-pointer transition-colors duration-300">
                Start Your Transformation
              </button>
              <button className="bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-800 whitespace-nowrap cursor-pointer border border-blue-500 transition-colors duration-300">
                Explore Our Solutions
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-6">Get in Touch</h2>
              <p className="text-xl text-gray-600 font-light">
                Let&apos;s discuss how we can accelerate your pharmaceutical innovation
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <div className="bg-white p-10 rounded-3xl shadow-lg border border-blue-100">
                  <form className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        placeholder="Your Company"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        placeholder="Tell us about your project..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 whitespace-nowrap cursor-pointer transition-colors duration-300"
                    >
                      Send Message
                    </button>
                  </form>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Phone</h3>
                  <p className="text-gray-700 font-light">+1 (555) 123-4567</p>
                </div>

                <div className="bg-white p-8 rounded-2xl shadow-lg border border-indigo-100">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-medium text-indigo-800 mb-2">Email</h3>
                  <p className="text-gray-700 font-light">ai.helixara@gmail.com</p>
                </div>

                <div className="bg-white p-8 rounded-2xl shadow-lg border border-emerald-100">
                  <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-medium text-emerald-800 mb-2">Office</h3>
                  <p className="text-gray-700 font-light">
                    123 Innovation Drive<br />
                    Boston, MA 02115<br />
                    United States
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative flex justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20multi-industry%20innovation%20workspace%20with%20diverse%20professionals%20collaborating%20on%20AI%20technology%20across%20various%20sectors%20including%20biotech%2C%20banking%2C%20labs%2C%20and%20healthcare%2C%20clean%20contemporary%20office%20environment%20with%20digital%20transformation%20displays%2C%20collaborative%20teams%20working%20with%20cloud%20computing%20and%20automation%20systems%2C%20professional%20diverse%20group%20in%20modern%20business%20attire%20actively%20discussing%20AI%20consulting%20frameworks%20with%20visible%20computer%20screens%20showing%20artificial%20intelligence%20metrics%2C%20modern%20office%20space%20with%20clean%20lines%20and%20advanced%20technology%20integration&width=600&height=480&seq=about-multi-industry&orientation=landscape"
                  alt="Leading Cross-Industry Innovation"
                  className="rounded-2xl shadow-2xl object-cover w-full h-80 object-center"
                />
              </div>

              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Pioneers in Physical AI and Biosensor Innovation
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Helixara specializes in the convergence of generative AI and advanced materials 
                  science, revolutionizing how organizations harness intelligent systems. We deliver 
                  strategic research collaboration and consulting services, pioneering cutting-edge 
                  sensor technologies for molecular detection and real-time health monitoring that 
                  transform pharmaceutical and biotechnology innovation.
                </p>
                <p className="text-lg text-gray-600 mb-8">
                  Our proprietary "Lab-in-a-Loop" platform leverages semiconducting organic frameworks 
                  to accelerate drug discovery and biotechnology breakthroughs for global partners. 
                  We've successfully guided over 150 organizations through complex transformation 
                  challenges, reducing operational inefficiencies by an average of 40% while maintaining 
                  the highest standards of innovation and compliance.
                </p>

                <div className="grid grid-cols-3 gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">150+</div>
                    <div className="text-sm text-gray-600">Projects Completed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">40%</div>
                    <div className="text-sm text-gray-600">Efficiency Improvement</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">98%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>

                <button className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 whitespace-nowrap cursor-pointer">
                  Meet Our Team
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Meet Our Team Section */}
        <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-6">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                Our leadership team combines decades of cross‑industry expertise with cutting‑edge AI
                innovation to guide organizations through transformative digital evolution
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Founder & CEO */}
              <div className="lg:col-span-3 flex justify-center mb-12">
                <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 max-w-md w-full text-center">
                  <div className="relative mb-6">
                    <img
                      src="https://static.readdy.ai/image/d80b109f077bb32eaa0599f7baacf71d/e3ab00be16a4cd75c3aa821bf93b2f29.jpeg"
                      alt="Kousik Paitandi - Founder &amp; CEO"
                      className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg object-top"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-star-fill text-white text-sm"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-medium text-blue-800 mb-2">
                    Kousik Paitandi, MBA, MS
                  </h3>
                  <p className="text-blue-600 font-medium mb-4">Founder &amp; CEO</p>
                  <p className="text-gray-700 mb-6 font-light leading-relaxed">
                    Visionary leader with extensive experience in AI‑driven digital transformation and strategic business
                    development. Holds an MBA and an MS in Computational Biology. Specializes in cross‑industry
                    AI implementation, pharmaceutical technology consulting, and building scalable intelligent systems that bridge
                    cutting‑edge research and practical business applications.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors">
                      <i className="ri-linkedin-fill text-blue-600 text-sm"></i>
                    </div>
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors">
                      <i className="ri-twitter-fill text-blue-600 text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chief Scientific Advisor */}
              <div className="lg:col-span-3 flex justify-center mb-12">
                <div className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100 max-w-md w-full text-center">
                  <div className="relative mb-6">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20male%20scientist%20Dr%20Rajendra%20Prasad%20in%20premium%20business%20attire%20standing%20in%20advanced%20materials%20science%20laboratory%2C%20confident%20researcher%20with%20PhD%20credentials%20wearing%20elegant%20blazer%20in%20state-of-the-art%20research%20facility%20with%20semiconducting%20materials%20and%20organometallic%20compounds%2C%20sophisticated%20portrait%20of%20Chief%20Scientific%20Advisor%20in%20clean%20laboratory%20environment%20with%20advanced%20equipment%2C%20professional%20headshot%20photography%20with%20warm%20lighting%20and%20contemporary%20scientific%20setting&width=128&height=128&seq=dr-rajendra-prasad-csa&orientation=squarish"
                      alt="Rajendra Prasad, PhD - Chief Scientific Advisor"
                      className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg object-top"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                      <i className="ri-flask-line text-white text-sm"></i>
                    </div>
                  </div>
                  <h3 className="text-2xl font-medium text-purple-800 mb-2">
                    Rajendra Prasad, PhD
                  </h3>
                  <p className="text-purple-600 font-medium mb-4">Chief Scientific Advisor</p>
                  <p className="text-gray-700 mb-6 font-light leading-relaxed">
                    An accomplished deep-tech researcher with 13+ years of experience, including elite fellowships at Kyoto University (JSPS) and Peking University. With 33 high-impact publications (h-index 17), he is a global expert in semiconducting COFs/MOFs and organometallic therapeutics. His breakthrough research in charge-transport devices (OFETs) and photoactive anticancer agents forms the technical bedrock of Helixara's "Lab-in-a-Loop" platform, bridging advanced materials science with real-time biological data acquisition.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-200 transition-colors">
                      <i className="ri-linkedin-fill text-purple-600 text-sm"></i>
                    </div>
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-200 transition-colors">
                      <i className="ri-twitter-fill text-purple-600 text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16 text-center">
              <div className="bg-white p-8 rounded-3xl max-w-4xl mx-auto border border-blue-100 shadow-lg">
                <h3 className="text-2xl font-medium text-blue-800 mb-4">
                  Join Our Advisory Network
                </h3>
                <p className="text-gray-700 mb-6 font-light leading-relaxed">
                  We&apos;re always looking for exceptional leaders and industry experts to join our
                  advisory network. If you&apos;re passionate about AI‑driven transformation and have
                  deep expertise in your field, we&apos;d love to connect.
                </p>
                <button className="bg-blue-600 text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 whitespace-nowrap cursor-pointer transition-colors duration-300">
                  Explore Advisory Opportunities
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Our Mission
              </h2>
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 bg-blue-100 rounded-full opacity-20"></div>
                </div>
                <blockquote className="relative text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed italic max-w-5xl mx-auto">
                  "Helixara delivers strategic consultancy and technical support to help organizations harness artificial intelligence and digital transformation. We empower clients across industries to enhance operational efficiency,
                  accelerate innovation, and advance digital health initiatives. Our mission
                  is to guide the responsible adoption of intelligent systems and personalized
                  wellbeing solutions — because living well is not a luxury — it&apos;s a science."
                </blockquote>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-compasses-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-blue-800 mb-3">
                  Strategic Consultancy
                </h3>
                <p className="text-gray-700">
                  Guiding organizations through AI integration and digital transformation with
                  expert strategic planning and implementation support
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-purple-800 mb-3">
                  Intelligent Systems
                </h3>
                <p className="text-gray-700">
                  Empowering responsible adoption of AI technologies across industries to enhance
                  operational efficiency and accelerate innovation
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-rose-800 mb-3">
                  Digital Wellbeing
                </h3>
                <p className="text-gray-700">
                  Advancing digital health initiatives through evidence‑based science and strategic
                  implementation
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                Our Vision
              </h2>
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-40 h-40 bg-blue-100 rounded-full opacity-20"></div>
                </div>
                <blockquote className="relative text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed italic max-w-5xl mx-auto">
                  "To be the trusted global partner that transforms how organizations integrate
                  artificial intelligence and digital technologies. We envision a future where
                  every industry leverages intelligent systems responsibly, where digital
                  transformation drives sustainable innovation, and where personalized wellbeing
                  solutions are accessible to all through evidence‑based science and strategic
                  implementation."
                </blockquote>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">
                  Global Partnership
                </h3>
                <p className="text-gray-700">
                  Building trusted relationships with organizations worldwide to facilitate
                  responsible AI adoption and digital transformation initiatives
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-teal-800 mb-3">
                  Responsible Innovation
                </h3>
                <p className="text-gray-700">
                  Ensuring ethical implementation of intelligent systems with compliance frameworks
                  that protect stakeholders and drive sustainable growth
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-cyan-800 mb-3">
                  Accessible Wellbeing
                </h3>
                <p className="text-gray-700">
                  Making evidence‑based digital health solutions accessible through strategic
                  consulting and technical implementation support
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' }}>
                    HELIXARA
                  </h3>
                  <p className="text-sm text-slate-600 font-medium tracking-widest uppercase">
                    Designing Life. Decoding Healing.
                  </p>
                </div>
                <p className="text-gray-600 mb-8 max-w-md font-light leading-relaxed">
                  AI-powered strategic consulting across industries, empowering organizations through
                  intelligent systems and digital transformation solutions
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-blue-50 cursor-pointer transition-colors duration-300">
                    <i className="ri-linkedin-fill text-gray-600"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-blue-50 cursor-pointer transition-colors duration-300">
                    <i className="ri-twitter-fill text-gray-600"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-blue-50 cursor-pointer transition-colors duration-300">
                    <i className="ri-facebook-fill text-gray-600"></i>
                  </a>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium mb-6 text-gray-800">Services</h4>
                <ul className="space-y-3 text-gray-600">
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Drug Discovery
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Clinical Trials
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Biosensors
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Regulatory Affairs
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-medium mb-6 text-gray-800">Company</h4>
                <ul className="space-y-3 text-gray-600">
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Careers
                    </a>
                  </li>               
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      News
                    </a>
                  </li>             
                  <li>
                    <a href="#" className="hover:text-blue-600 cursor-pointer font-light transition-colors duration-300">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Medical Disclaimer and Experimental Notice */}
            <div className="border-t border-gray-100 mt-12 pt-8">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-alert-line text-amber-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-amber-800 mb-3">
                      Not a Substitute for Professional Medical Advice
                    </h4>
                    <p className="text-amber-700 font-light leading-relaxed">
                      Information provided is for research and educational purposes only. Always consult a qualified healthcare professional for medical advice or treatment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-information-line text-blue-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-blue-800 mb-3">
                      Experimental Website Notice
                    </h4>
                    <p className="text-blue-700 font-light leading-relaxed">
                      This website is under development with AI-generated content for demonstration purposes. All data and characters are hypothetical and do not represent real entities.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-500 text-sm font-light">
                © 2024 Helixara. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-500 hover:text-blue-600 text-sm cursor-pointer font-light transition-colors duration-300">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-500 hover:text-blue-600 text-sm cursor-pointer font-light transition-colors duration-300">
                  Terms of Service
                </a>
                <a href="https://readdy.ai/?origin=logo" className="text-gray-500 hover:text-blue-600 text-sm cursor-pointer font-light transition-colors duration-300">
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
